import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import { MdAdd, MdEdit, MdDelete, MdBadge } from "react-icons/md";
import { createApi } from "../../../../core/api";

export const RoleCrud = () => {
  const api = useMemo(() => createApi(), []);
  const { setPageMenus } = useOutletContext<any>();

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);

  const [name, setName] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.Role.getAll()
      .then((r) => {
        if (r?.ok) {
          const list = r.data?.data ?? r.data ?? [];
          setData(Array.isArray(list) ? list : []);
        }
      })
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => {
    setPageMenus([
      {
        label: "Roles",
        items: [{ label: "Roles", route: "/dashboard/roles" }],
      },
    ]);
    load();
    return () => setPageMenus([]);
  }, [load, setPageMenus]);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setError(null);
    setOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    setName(row?.name ?? "");
    setError(null);
    setOpen(true);
  };

  const submit = async () => {
    if (!name.trim() || saving) return;

    setSaving(true);
    setError(null);

    try {
      if (editing) {
        const r = await api.Role.update(editing.id, { name });
        if (!r?.ok) throw new Error(r?.message || "Update failed");
      } else {
        const r = await api.Role.create({ name });
        if (!r?.ok) throw new Error(r?.message || "Create failed");
      }

      setOpen(false);
      load();
    } catch (e: any) {
      setError(e?.message || "Unexpected error occurred");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setDeleteError(null);
  };

  const doDelete = async () => {
    if (!deleteId || deleting) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      const r = await api.Role.delete(deleteId);
      if (!r?.ok) throw new Error(r?.message || "Delete failed");

      setDeleteId(null);
      load();
    } catch (e: any) {
      setDeleteError(e?.message || "Unexpected error occurred");
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo<any[]>(
    () => [
      {
        key: "id",
        title: "ID",
        minWidth: 80,
        accessor: (r: any) => r.id,
      },
      {
        key: "name",
        title: "Role Name",
        minWidth: 260,
        accessor: (r: any) => r.name,
        render: (_: any, v: string) => (
          <Badge
            icon={MdBadge}
            label={v}
            color="#2563EB"
            bg="rgba(37,99,235,.15)"
          />
        ),
      },
      {
        key: "actions",
        title: "Actions",
        minWidth: 200,
        render: (row: any) => (
          <GroupButton
            items={[
              {
                id: "edit",
                icon: MdEdit,
                color: "#2563EB",
                onClick: () => openEdit(row),
              },
              {
                id: "delete",
                icon: MdDelete,
                color: "#DC2626",
                onClick: () => confirmDelete(row.id),
              },
            ]}
          />
        ),
      },
    ],
    []
  );

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Roles</div>
          <div style={S.subTitle}>Manage system roles</div>
        </div>

        <Button variant="primary" icon={MdAdd} onClick={openCreate}>
          Create Role
        </Button>
      </div>

      <Table
        tableKey="roles"
        loading={loading}
        columns={columns}
        data={data}
        features={{
          sortable: true,
          filterable: true,
          pagination: true,
          persistence: true,
        }}
      />

      <Modal open={open} onClose={() => (!saving ? setOpen(false) : null)}>
        <div style={S.modalTitle}>
          {editing ? "Update Role" : "Create Role"}
        </div>

        <div style={S.form}>
          <label style={S.label}>Role Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={S.input}
            disabled={saving}
          />

          {error && <div style={S.error}>{error}</div>}
        </div>

        <div style={S.actions}>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={saving}
            disabled={saving}
          >
            Save
          </Button>
        </div>
      </Modal>

      <Modal
        open={!!deleteId}
        onClose={() => (!deleting ? setDeleteId(null) : null)}
      >
        <div style={S.modalTitle}>Delete Role</div>

        <div style={{ fontSize: 14, color: "#334155" }}>
          Are you sure you want to delete this role?
        </div>

        {deleteError && <div style={S.error}>{deleteError}</div>}

        <div style={S.actions}>
          <Button
            variant="ghost"
            onClick={() => setDeleteId(null)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={doDelete}
            loading={deleting}
            disabled={deleting}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  wrap: {
    position: "absolute",
    inset: 0,
    padding: 20,
    background: "#F8FAFC",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  title: { fontSize: 18, fontWeight: 600, color: "#0F172A" },
  subTitle: {
    fontSize: 13,
    color: "#64748B",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 800,
    marginBottom: 14,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  label: {
    fontSize: 12,
    fontWeight: 700,
    color: "#334155",
  },
  input: {
    height: 42,
    borderRadius: 10,
    border: "1px solid #CBD5E1",
    padding: "0 12px",
    fontSize: 14,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 20,
  },
  error: {
    marginTop: 8,
    fontSize: 12,
    fontWeight: 600,
    color: "#DC2626",
  },
};
